import React, { useRef, useState, useEffect } from 'react';
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import useToken from "./useToken";
import { Toast } from 'primereact/toast';
import SpinnerLoading from '../sampanganimation/SpinnerLoading';
import ValidToken from "./middleware/ValidToken";
import { useOtpPushListener } from "../../hooks/useOtpPushListener";

const OTPComponent = ({ length = 6, users, onResend, className = '' }) => {
    const inputRef = useRef(Array(length).fill(null));
    const [OTP, setOTP] = useState(Array(length).fill(''));
    const [otp, setOtp] = useState('');
    const [timeLeft, setTimeLeft] = useState(300);
    const [canResend, setCanResend] = useState(false);
    const [shake, setShake] = useState(false);
    const [suggestedOtp, setSuggestedOtp] = useState(null);
    const toast = useRef(null);
    const { token, setToken } = useToken();
    const navigate = useNavigate();

    // Dengerin push notifikasi yang masuk
    const { receivedCode } = useOtpPushListener();

    // 🌟 KETIKA KODE MASUK:
    // JANGAN langsung isi inputan secara paksa!
    // Simpan sebagai saran, dan coba salin ke clipboard agar Gboard di Android langsung menampilkan chip saran di atas keyboard.
    useEffect(() => {
        if (receivedCode) {
            const clean = String(receivedCode).replace(/\D/g, '').slice(0, length);
            if (clean.length === length) {
                setSuggestedOtp(clean);

                // Copy ke clipboard agar keyboard HP (Gboard Android) langsung memunculkan tombol chip saran OTP di atas keyboard
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    navigator.clipboard.writeText(clean).catch(() => { });
                }
            }
        }
    }, [receivedCode, length]);

    // Timer countdown
    useEffect(() => {
        if (timeLeft <= 0) {
            setCanResend(true);
            return;
        }

        const timer = setInterval(() => {
            setTimeLeft((prev) => prev - 1);
        }, 1000);

        return () => clearInterval(timer);
    }, [timeLeft]);

    // Helper untuk mengisi semua kotak sekaligus saat saran diklik
    const applyOtpCode = (code) => {
        const cleaned = String(code).replace(/\D/g, '').slice(0, length);
        const digits = cleaned.split('');
        const newOTP = Array(length).fill('');
        digits.forEach((d, i) => {
            newOTP[i] = d;
        });
        setOTP(newOTP);
        setOtp(cleaned);
        setSuggestedOtp(null);

        // Pindahkan fokus ke kotak terakhir
        inputRef.current[Math.min(digits.length - 1, length - 1)]?.focus();
    };

    // 🔹 PENANGANAN PERUBAHAN TEKS & ONE-TIME-CODE DARI KEYBOARD
    const handleTextChange = (value, index) => {
        const cleaned = value.replace(/\D/g, '');

        // 🌟 KUNCI AUTOCOMPLETE KEYBOARD HP (Gboard / iOS QuickType):
        // Ketika pengguna menekan saran kode yang muncul di atas keyboard,
        // seluruh 6 digit akan masuk sekaligus ke kotak input (> 1 digit).
        // Fungsi ini mendeteksi lalu mendistribusikannya ke seluruh kotak 1 s.d. 6.
        if (cleaned.length > 1) {
            applyOtpCode(cleaned);
            return;
        }

        // Penanganan ketik manual 1 digit biasa
        const newOTP = [...OTP];
        newOTP[index] = cleaned;
        setOTP(newOTP);

        const fullOtp = newOTP.join('');
        setOtp(fullOtp);

        if (cleaned && index < length - 1) {
            inputRef.current[index + 1]?.focus();
        }

        if (!cleaned && index > 0) {
            inputRef.current[index - 1]?.focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && OTP[index] === '' && index > 0) {
            inputRef.current[index - 1]?.focus();
        }

        if (e.key === 'ArrowLeft' && index > 0) {
            inputRef.current[index - 1]?.focus();
        }

        if (e.key === 'ArrowRight' && index < length - 1) {
            inputRef.current[index + 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasteData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
        if (pasteData.length > 0) {
            applyOtpCode(pasteData);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (otp.length !== length) {
            setShake(true);
            setTimeout(() => setShake(false), 500);
            return;
        }

        SpinnerLoading(true);

        axios
            .post(global.config.backend + "/api/otpvalidate", {
                user_id: users.id,
                otp: otp
            })
            .then((res) => {
                SpinnerLoading(false);

                if (res.data.status) {
                    setToken(res.data.data);
                    ValidToken().then((valid) => {
                        if (valid.status) {
                            if (valid.data.users[0].is_apel_leader == '1') {
                                navigate("/unit-kerja/Sekretariat?page=apel-pagi");
                            } else {
                                navigate('/daily-activity');
                            }
                        }
                    });
                } else {
                    setShake(true);
                    setTimeout(() => setShake(false), 500);

                    if (res.data.errors && res.data.errors.length > 0) {
                        res.data.errors.map((item) => {
                            toast.current.show({ severity: 'warn', detail: item });
                        });
                    } else {
                        toast.current.show({ severity: 'warn', detail: res.data.massage || res.data.message });
                    }
                }
            })
            .catch((res) => {
                console.log(res);
                SpinnerLoading(false);
                setShake(true);
                setTimeout(() => setShake(false), 500);
                toast.current.show({ severity: 'warn', detail: 'Error connect to services' });
            });
    };

    const handleResendCode = (e) => {
        setTimeLeft(300);
        setCanResend(false);
        setOTP(Array(length).fill(''));
        setOtp('');
        setSuggestedOtp(null);
        onResend(e);
    };

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
    };

    const animationStyles = `
        .otp-container {
            width: 100%;
        }

        .otp-card {
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(20px);
            border-radius: 24px;
            padding: 40px;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
            border: 1px solid rgba(255, 255, 255, 0.3);
            transform: translateY(0);
            opacity: 1;
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .otp-card.shake {
            animation: shake 0.5s ease-in-out;
        }

        .otp-suggestion-bar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            background: linear-gradient(135deg, #eff6ff, #dbeafe);
            border: 1.5px dashed #3b82f6;
            border-radius: 14px;
            padding: 10px 16px;
            margin-bottom: 20px;
            cursor: pointer;
            transition: all 0.2s ease;
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
            animation: slideDown 0.3s ease-out;
        }

        .otp-suggestion-bar:hover {
            background: #dbeafe;
            transform: translateY(-1px);
        }

        .otp-suggestion-left {
            display: flex;
            align-items: center;
            gap: 10px;
            font-size: 13px;
            color: #1e3a8a;
            font-weight: 500;
        }

        .otp-suggestion-code {
            font-family: monospace;
            font-size: 16px;
            font-weight: 700;
            background: #1e40af;
            color: #ffffff;
            padding: 3px 10px;
            border-radius: 8px;
            letter-spacing: 2px;
        }

        .otp-suggestion-btn {
            font-size: 12px;
            font-weight: 600;
            color: #2563eb;
            background: white;
            padding: 5px 12px;
            border-radius: 8px;
            border: 1px solid #93c5fd;
        }

        .otp-inputs {
            display: flex;
            justify-content: center;
            gap: 12px;
            margin: 24px 0 32px 0;
        }

        .otp-input-wrapper {
            position: relative;
        }

        .otp-input {
            width: 50px;
            height: 50px;
            border: 2px solid rgba(4, 170, 255, 0.2);
            border-radius: 12px;
            background: rgba(255, 255, 255, 0.9);
            text-align: center;
            font-size: 24px;
            font-weight: 600;
            color: #002461;
            transition: all 0.3s ease;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
        }

        .otp-input:focus {
            outline: none;
            border-color: #04a;
            transform: scale(1.05);
            box-shadow: 0 8px 25px rgba(4, 170, 255, 0.2);
        }

        .otp-input.filled {
            border-color: #04a;
            background: rgba(4, 170, 255, 0.05);
        }

        .verify-btn {
            margin-top: 16px;
        }

        .resend-section {
            text-align: center;
            margin-bottom: 20px;
        }

        .resend-btn {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            background: none;
            border: none;
            color: #04a;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            padding: 8px 16px;
            border-radius: 8px;
            transition: all 0.3s ease;
        }

        .resend-btn:hover {
            background: rgba(4, 170, 255, 0.1);
            transform: translateY(-2px);
        }

        .timer {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            color: #666;
            font-size: 14px;
        }

        .timer strong {
            color: #002461;
            font-weight: 600;
        }

        @keyframes shake {
            0%, 100% { transform: translateX(0); }
            10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
            20%, 40%, 60%, 80% { transform: translateX(5px); }
        }

        @keyframes slideDown {
            from { opacity: 0; transform: translateY(-8px); }
            to { opacity: 1; transform: translateY(0); }
        }

        @media (max-width: 768px) {
            .otp-input {
                width: 46px;
                height: 46px;
                font-size: 20px;
            }
            .otp-inputs {
                gap: 8px;
            }
        }

        @media (max-width: 480px) {
            .otp-input {
                width: 38px;
                height: 38px;
                font-size: 18px;
            }
            .otp-inputs {
                gap: 6px;
            }
        }
    `;

    return (
        <>
            <div className={`otp-container ${className}`}>
                <Toast ref={toast} />
                <div className={`otp-card ${shake ? 'shake' : ''}`}>
                    <div className="card-header">
                        <h2 className="card-title">Verifikasi OTP</h2>
                        <p className="card-subtitle">
                            Masukkan 6-digit kode yang telah terkirim ke perangkat Anda
                        </p>
                    </div>

                    {/* 💡 SARAN KODE: Muncul jika kode terdeteksi dari notifikasi, tapi TIDAK mengisi otomatis sebelum diklik */}
                    {suggestedOtp && !otp && (
                        <div
                            className="otp-suggestion-bar"
                            onClick={() => applyOtpCode(suggestedOtp)}
                            title="Klik untuk mengisi inputan"
                        >
                            <div className="otp-suggestion-left">
                                <span>🔔 Kode dari Notifikasi:</span>
                                <span className="otp-suggestion-code">{suggestedOtp}</span>
                            </div>
                            <button type="button" className="otp-suggestion-btn">
                                Ketuk untuk Masukkan
                            </button>
                        </div>
                    )}

                    <form className="otp-form" onSubmit={handleSubmit} onPaste={handlePaste}>
                        <div className="form-group">
                            <div className="otp-inputs">
                                {Array.from({ length }, (_, index) => (
                                    <div key={index} className="otp-input-wrapper">
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            // 🌟 ATRIBUT AGAR MUNCUL DI ATAS KEYBOARD HP:
                                            // 1. Kotak pertama memiliki autoComplete="one-time-code"
                                            // 2. maxLength kotak pertama diset ke length (6 digit) agar saat user
                                            //    menekan saran di bar keyboard (Gboard / iOS), 6 digit masuk utuh ke onChange
                                            autoComplete={index === 0 ? "one-time-code" : "off"}
                                            maxLength={index === 0 ? length : 1}
                                            autoFocus={index === 0}
                                            value={OTP[index]}
                                            onChange={(e) => handleTextChange(e.target.value, index)}
                                            onKeyDown={(e) => handleKeyDown(e, index)}
                                            onFocus={(e) => e.target.select()}
                                            ref={(ref) => (inputRef.current[index] = ref)}
                                            className={`otp-input ${OTP[index] ? 'filled' : ''}`}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="form-group">
                            <button type="submit" className="modern-btn primary verify-btn">
                                <span>Verifikasi</span>
                                <i className="ti-check"></i>
                            </button>
                        </div>
                    </form>

                    <div className="card-footer">
                        <div className="resend-section">
                            {canResend ? (
                                <button
                                    className="resend-btn"
                                    onClick={handleResendCode}
                                >
                                    <i className="ti-reload"></i>
                                    <span>Kirim Ulang OTP</span>
                                </button>
                            ) : (
                                <div className="timer">
                                    <i className="ti-timer"></i>
                                    <span>Kirim ulang dalam <strong>{formatTime(timeLeft)}</strong></span>
                                </div>
                            )}
                        </div>

                        <p className="copyright mb-0">
                            {new Date().getFullYear()} &copy; Dinas Kominfo Sampang
                        </p>
                    </div>
                </div>
            </div>
            <style>{animationStyles}</style>
        </>
    );
};

export default OTPComponent;
